import React, { useEffect, useState } from "react";
import config from "../../config";
import { getAuth, signInAnonymously } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase";

const RequiredStar = () => (
  <span style={{ color: "#dc2626", marginLeft: 4 }}>*</span>
);

const colors = [
  "Red",
  "Blue",
  "Green",
  "Black",
  "White",
  "Yellow",
  "Orange",
  "Pink",
  "Purple",
  "Brown",
  "Gray",
  "Navy",
  "Maroon",
  "Dark Blue",
  "Light Blue",
  "Beige",
  "Cream",
  "Gold",
  "Silver",
  "Olive",
];

export default function UpdateProduct({ product, onClose, onProductUpdated }) {
  const [currentStep, setCurrentStep] = useState(1);

  const [categories, setCategories] = useState([]); // from backend
  const [subCategories, setSubCategories] = useState([]); // from backend

  const [allSizes, setAllSizes] = useState([]);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [sizesLoading, setSizesLoading] = useState(false);
  const [sizesError, setSizesError] = useState("");

  const sizeOptions = availableSizes.length > 0 ? availableSizes : allSizes;

  const [form, setForm] = useState({
    productName: product.productName || "",
    color: product.color || "",
    productDescription: product.productDescription || "",
    active: product.active ?? true,
    hsnCode: product.hsnCode || "",
    categoryName: product.category?.name || product.categoryName || "",
    subCategoryName:
      product.subCategory?.subCategoryName || product.subCategoryName || "",
    details: { ...(product.details || {}) },
    variants:
      product.variants?.map((v) => ({
        ...v,
        variantId: v.variantId || v.id,
        sizeName: v.size?.code || v.sizeName || v.size?.name || "",
        vendorId: v.vendor?.vendorId || v.vendorId,
        offerPrice:
          v.offerPrice ||
          (parseFloat(v.originalPrice) * (1 - (v.discount || 0) / 100)),
      })) || [],
    images: product.images || [],
  });


  // Image views for new uploads only
  const [imageViewTypes, setImageViewTypes] = useState({
    "Main View": null,
    "Front View": null,
    "Back View": null,
    "Left Side View": null,
    "Right Side View": null,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  /* ===========================
     FETCH CATEGORIES + SUBCATEGORIES
     =========================== */

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${config.baseUrl}/api/categories`);
        if (res.ok) {
          const data = await res.json();
          setCategories(data || []);
        }
      } catch (err) {
        console.error("Failed to fetch categories:", err);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!form.categoryName) {
      setSubCategories([]);
      return;
    }

    const fetchSubCategories = async () => {
      try {
        const encoded = encodeURIComponent(form.categoryName);
        const res = await fetch(
          `${config.baseUrl}/api/subCategories/category/${encoded}`
        );
        if (res.ok) {
          const data = await res.json();
          setSubCategories(data || []);
        } else {
          setSubCategories([]);
        }
      } catch (err) {
        console.error("Failed to fetch subcategories:", err);
        setSubCategories([]);
      }
    };

    fetchSubCategories();
  }, [form.categoryName]);

  /* ===========================
     FETCH SIZES
     =========================== */

  // all sizes fallback
  useEffect(() => {
    const fetchAllSizes = async () => {
      try {
        const res = await fetch(`${config.baseUrl}/api/sizes`);
        if (res.ok) {
          let data = await res.json();
          data = (data || []).sort(
            (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
          );
          setAllSizes(data);
        }
      } catch (err) {
        console.error("Failed to fetch all sizes", err);
      }
    };
    fetchAllSizes();
  }, []);

  // subCategory-specific sizes
  useEffect(() => {
    if (!form.categoryName || !form.subCategoryName) {
      setAvailableSizes([]);
      setSizesError("");
      return;
    }

    const fetchSizesForSubCategory = async () => {
      setSizesLoading(true);
      setSizesError("");
      try {
        const params = new URLSearchParams({
          categoryName: form.categoryName,
          subCategoryName: form.subCategoryName,
        });
        const res = await fetch(
          `${config.baseUrl}/api/subCategorySizes?${params.toString()}`
        );
        if (res.ok) {
          let data = await res.json();
          data = (data || []).sort(
            (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
          );
          setAvailableSizes(data);
          if (!data || data.length === 0) {
            setSizesError(
              "No specific sizes configured for this sub-category. Showing all sizes as fallback."
            );
          }
        } else {
          setAvailableSizes([]);
          setSizesError(
            "Unable to load sizes for this sub-category. Showing all sizes as fallback."
          );
        }
      } catch (err) {
        console.error("Failed to fetch subCategory sizes:", err);
        setAvailableSizes([]);
        setSizesError(
          "Failed to load sub-category sizes. Showing all sizes as fallback."
        );
      } finally {
        setSizesLoading(false);
      }
    };

    fetchSizesForSubCategory();
  }, [form.categoryName, form.subCategoryName]);

  /* ===========================
     BASIC HANDLERS
     =========================== */

  const handleChange = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleDetailsChange = (field, value) =>
    setForm((prev) => ({
      ...prev,
      details: { ...(prev.details || {}), [field]: value },
    }));

  /* ===========================
     VARIANTS
     =========================== */

const handleVariantChange = (originalIdx, field, value) => {
  const updated = [...form.variants];

  // 🔒 Prevent duplicate size variants
  if (field === "sizeName") {
    const exists = updated.some(
      (v, i) => i !== originalIdx && v.sizeName === value
    );
    if (exists) {
      alert("This size already exists. Please choose a different size.");
      return;
    }
  }

  updated[originalIdx] = {
    ...updated[originalIdx],
    [field]: value,
  };

  // Auto-calculate offer price
  if (field === "discount" || field === "originalPrice") {
    const originalPrice =
      parseFloat(updated[originalIdx].originalPrice) || 0;
    const discount =
      parseFloat(updated[originalIdx].discount) || 0;

    updated[originalIdx].offerPrice = (
      originalPrice * (1 - discount / 100)
    ).toFixed(2);
  }

  setForm((prev) => ({ ...prev, variants: updated }));
};

  const handleRemoveVariant = (idx) => {
  setForm((prev) => ({
    ...prev,
    variants: prev.variants.filter((_, i) => i !== idx),
  }));
};

  const handleAddVariant = () => {
    setForm((prev) => ({
      ...prev,
      variants: [
        ...prev.variants,
        {
          variantId: null,     // 👈 VERY IMPORTANT
          sku: "",
          sizeName: "",
          originalPrice: "",
          discount: "",
          offerPrice: "",
          stockQuantity: "",
          color: "",
          isNew: true          // 👈 Optional but very helpful
        },
      ],
    }));
  };
  /* ===========================
     IMAGES (NEW UPLOADS)
     =========================== */

  const handleImageUploadForView = (viewType, file) => {
    if (!file) return;
    setImageViewTypes((prev) => ({
      ...prev,
      [viewType]: file,
    }));
  };

  const removeImageFromView = (viewType) => {
    setImageViewTypes((prev) => ({
      ...prev,
      [viewType]: null,
    }));
  };

  /* ===========================
     STEP NAVIGATION
     =========================== */

  const goToNextStep = () => setCurrentStep((prev) => Math.min(prev + 1, 4));
  const goToPrevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  /* ===========================
     STEP 3: SAVE PRODUCT (NO IMAGES)
     =========================== */

  const handleSaveProductAtStep3 = async () => {
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const productId = product.id || product.productId;
      if (!productId) {
        throw new Error("Product ID missing.");
      }

      // Basic validation: same as CreateProductForm
      if (
        !form.productName ||
        !form.categoryName ||
        !form.subCategoryName ||
        !form.hsnCode
      ) {
        throw new Error(
          "Please fill all required fields: Name, Category, Sub Category, HSN Code."
        );
      }

      const validVariants = form.variants.filter(
        (v) => v.sizeName && v.sku && v.originalPrice !== ""
      );

      if (validVariants.length === 0) {
        throw new Error("Please add at least one valid variant.");
      }

      const payload = {
        productName: form.productName,
        productDescription: form.productDescription,
        active: form.active,
        categoryName: form.categoryName,
        subCategoryName: form.subCategoryName,
        hsnCode: form.hsnCode,
        details: form.details || {},
        variants: validVariants.map((v) => ({
          variantId: v.variantId,
          sku: v.sku,
          sizeName: v.sizeName,
          originalPrice: parseFloat(v.originalPrice),
          discount: parseFloat(v.discount || 0),
          offerPrice: parseFloat(v.offerPrice || 0),
          stockQuantity: parseInt(v.stockQuantity || 0, 10),
          vendorId: v.vendorId,
          color: v.color || null,   // ✅ ONLY variant color
        })),

        // Images are managed in Step 4 via separate endpoint
        images: [],
      };

      const response = await fetch(
        `${config.baseUrl}/api/products/${productId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to update product");
      }

      setSuccess(
        "Product details and variants updated successfully! Now you can manage images."
      );
      setTimeout(() => {
        goToNextStep();
        setSuccess("");
      }, 800);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ===========================
     STEP 4: SAVE NEW IMAGES (OPTIONAL)
     =========================== */

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const productId = product.id || product.productId;
      if (!productId) {
        throw new Error("Product ID missing.");
      }

      const allImages = Object.entries(imageViewTypes)
        .filter(([, file]) => file)
        .map(([viewType, file]) => ({ viewType, file }));

      let uploadedImagesPayload = [];

      if (allImages.length > 0) {
        const auth = getAuth();
        if (!auth.currentUser) {
          await signInAnonymously(auth);
        }

        const userId = auth.currentUser.uid;
        const timestampBase = Date.now();

        const uploaded = await Promise.all(
          allImages.map(async ({ viewType, file }, idx) => {
            const filename = `updated_${timestampBase}_${idx}.jpg`;
            const storageRef = ref(
              storage,
              `product-images/${userId}/${productId}/${filename}`
            );

            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);

            return {
              imageUrl: url,
              imageType: viewType,
              displayOrder: idx + 1,
              color: null,
            };
          })
        );

        uploadedImagesPayload = uploaded;

        const imageResponse = await fetch(
          `${config.baseUrl}/api/productimages/product/${productId}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(uploadedImagesPayload),
          }
        );

        if (!imageResponse.ok) {
          const errorData = await imageResponse.json().catch(() => ({}));
          console.warn(
            "Warning: Images saved to Firebase but backend upload had issues:",
            errorData
          );
        }
      }

      let msg = "Product updated successfully.";
      if (uploadedImagesPayload.length > 0) {
        msg += ` ${uploadedImagesPayload.length} new image(s) added.`;
      } else {
        msg += " No new images uploaded.";
      }

      setSuccess(msg);
      setTimeout(() => {
        onProductUpdated && onProductUpdated();
        onClose && onClose();
      }, 1000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ===========================
     RENDER
     =========================== */

  return (
    <div className="modal-overlay">
      <form className="modal-content large-modal" onSubmit={handleSubmit}>
        <div className="modal-header">
          <h2>Update Product</h2>
          <div className="step-indicator">
            {[1, 2, 3, 4].map((step) => (
              <span
                key={step}
                className={`${step === currentStep ? "active" : ""} ${step < currentStep ? "completed" : ""
                  }`}
                onClick={() => setCurrentStep(step)}
                style={{ cursor: "pointer" }}
              >
                {step}
              </span>
            ))}
          </div>
          <button type="button" className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="form-section">
              <h3>Step 1: Basic Product Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>
                    Product Name <RequiredStar />
                  </label>
                  <input
                    value={form.productName}
                    onChange={(e) =>
                      handleChange("productName", e.target.value)
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    Category <RequiredStar />
                  </label>
                  <select
                    value={form.categoryName}
                    onChange={(e) =>
                      handleChange("categoryName", e.target.value)
                    }
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.categoryId} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>
                    Sub Category <RequiredStar />
                  </label>
                  <select
                    value={form.subCategoryName}
                    onChange={(e) =>
                      handleChange("subCategoryName", e.target.value)
                    }
                    disabled={!form.categoryName}
                    required
                  >
                    <option value="">Select Sub Category</option>
                    {subCategories.map((sc) => (
                      <option key={sc.subCategoryId} value={sc.subCategoryName}>
                        {sc.subCategoryName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>
                    HSN Code <RequiredStar />
                  </label>
                  <input
                    value={form.hsnCode}
                    onChange={(e) =>
                      handleChange("hsnCode", e.target.value)
                    }
                    placeholder="e.g., 6109"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Rating</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    value={form.rating}
                    onChange={(e) =>
                      handleChange("rating", e.target.value)
                    }
                    placeholder="0.0 to 5.0"
                  />
                </div>

                <div className="form-group">
                  <label>Active</label>
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) =>
                      handleChange("active", e.target.checked)
                    }
                  />
                </div>

                <div className="form-group full-width">
                  <label>Product Description</label>
                  <textarea
                    value={form.productDescription}
                    onChange={(e) =>
                      handleChange("productDescription", e.target.value)
                    }
                    rows={3}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={goToNextStep}
                >
                  Next: Details
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Additional Details */}
          {currentStep === 2 && (
            <div className="form-section">
              <h3>Step 2: Additional Product Details</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Fabric</label>
                  <input
                    value={form.details.fabric || ""}
                    onChange={(e) =>
                      handleDetailsChange("fabric", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Pattern</label>
                  <input
                    value={form.details.pattern || ""}
                    onChange={(e) =>
                      handleDetailsChange("pattern", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Neck Type</label>
                  <input
                    value={form.details.neck || ""}
                    onChange={(e) =>
                      handleDetailsChange("neck", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Sleeve Length</label>
                  <input
                    value={form.details.sleeveLength || ""}
                    onChange={(e) =>
                      handleDetailsChange("sleeveLength", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Fit</label>
                  <input
                    value={form.details.fit || ""}
                    onChange={(e) =>
                      handleDetailsChange("fit", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Occasion</label>
                  <input
                    value={form.details.occasion || ""}
                    onChange={(e) =>
                      handleDetailsChange("occasion", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Brand</label>
                  <input
                    value={form.details.brand || ""}
                    onChange={(e) =>
                      handleDetailsChange("brand", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Country of Origin</label>
                  <input
                    value={form.details.countryOfOrigin || ""}
                    onChange={(e) =>
                      handleDetailsChange(
                        "countryOfOrigin",
                        e.target.value
                      )
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Material</label>
                  <input
                    value={form.details.material || ""}
                    onChange={(e) =>
                      handleDetailsChange("material", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Care Instructions</label>
                  <input
                    value={form.details.careInstructions || ""}
                    onChange={(e) =>
                      handleDetailsChange(
                        "careInstructions",
                        e.target.value
                      )
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Features</label>
                  <input
                    value={form.details.features || ""}
                    onChange={(e) =>
                      handleDetailsChange("features", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Season</label>
                  <input
                    value={form.details.season || ""}
                    onChange={(e) =>
                      handleDetailsChange("season", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={goToPrevStep}>
                  Back
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={goToNextStep}
                >
                  Next: Variants & Pricing
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Variants & Pricing */}
          {currentStep === 3 && (
            <div className="form-section">
              <h3>Step 3: Variants, SKU & Pricing</h3>
              <p className="step-description">
                Configure variants with pricing information. Click{" "}
                <b>Save Product &amp; Next</b> to update and move to images.
              </p>

              {sizesLoading && (
                <div
                  className="error-message"
                  style={{
                    background: "#dbeafe",
                    color: "#1d4ed8",
                    borderColor: "#bfdbfe",
                  }}
                >
                  Loading sizes for this sub-category…
                </div>
              )}
              {sizesError && (
                <div className="error-message" style={{ marginBottom: 10 }}>
                  {sizesError}
                </div>
              )}

              <div className="variants-container">
                {form.variants
                  .map((variant, originalIdx) => ({ variant, originalIdx }))
                  .map(({ variant, originalIdx }, idx) => (
                    <div key={originalIdx} className="variant-card">
                      <div className="variant-header">
                        <h5>
                          Variant {idx + 1}
                          {variant.color && (
                            <span style={{ marginLeft: 8, color: "#555" }}>
                              ({variant.color})
                            </span>
                          )}
                        </h5>

                        {!variant.variantId && (
                          <button
                            type="button"
                            className="btn-remove"
                            onClick={() => handleRemoveVariant(originalIdx)}
                          >
                            Remove
                          </button>
                        )}

                      </div>
                      <div className="variant-grid">
                        <div className="form-group">
                          <label>
                            Size <RequiredStar />
                          </label>
                          <select
                            value={variant.sizeName || ""}
                            onChange={(e) =>
                              handleVariantChange(
                                originalIdx,
                                "sizeName",
                                e.target.value
                              )
                            }
                            required
                            disabled={sizeOptions.length === 0}
                          >
                            <option value="">Select Size</option>
                            {sizeOptions.map((size) => (
                              <option key={size.sizeId} value={size.code}>
                                {size.code}
                                {size.name ? ` - ${size.name}` : ""}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Color</label>
                          <select
                            value={variant.color || ""}
                            onChange={(e) =>
                              handleVariantChange(originalIdx, "color", e.target.value)
                            }
                          >
                            <option value="">No color</option>
                            {colors.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group">
                          <label>SKU <RequiredStar /></label>
                          <input
                            value={variant.sku || ""}
                            onChange={(e) =>
                              handleVariantChange(originalIdx, "sku", e.target.value)
                            }
                            placeholder="Product SKU"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>
                            Original Price <RequiredStar />
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={variant.originalPrice}
                            onChange={(e) =>
                              handleVariantChange(
                                originalIdx,
                                "originalPrice",
                                e.target.value
                              )
                            }
                            placeholder="0.00"
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Discount (%)</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={variant.discount || ""}
                            onChange={(e) =>
                              handleVariantChange(
                                originalIdx,
                                "discount",
                                e.target.value
                              )
                            }
                            placeholder="0"
                          />
                        </div>
                        <div className="form-group">
                          <label>Offer Price</label>
                          <input
                            type="number"
                            step="0.01"
                            value={variant.offerPrice || ""}
                            readOnly
                            className="readonly-field"
                          />
                        </div>
                        <div className="form-group">
                          <label>
                            Stock Quantity <RequiredStar />
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={variant.stockQuantity}
                            onChange={(e) =>
                              handleVariantChange(
                                originalIdx,
                                "stockQuantity",
                                e.target.value
                              )
                            }
                            placeholder="0"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                <button
                  type="button"
                  className="btn-add-variant"
                  onClick={handleAddVariant}
                >
                  + Add Another Variant
                </button>
              </div>

              <div className="form-actions">
                <button type="button" onClick={goToPrevStep}>
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleSaveProductAtStep3}
                  disabled={loading}
                  className="btn-primary"
                >
                  {loading ? "Saving Product..." : "Save Product & Next"}
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Images */}
          {currentStep === 4 && (
            <div className="form-section">
              <h3>Step 4: Product Images</h3>
              <p className="step-description">
                Existing images are shown below. You can upload new images for
                different views (optional).
              </p>

              <div>
                <h4>Existing Images</h4>
                {form.images && form.images.length > 0 ? (
                  <div className="product-images-grid">
                    {form.images.map((img, idx) => (
                      <div key={idx} className="image-item">
                        <img
                          src={img.image_url || img.imageUrl}
                          alt={`Product ${idx}`}
                          loading="lazy"
                        />
                        <div className="image-info">
                          <small className="image-type">
                            {img.image_type || img.imageType || "Image"}
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>No existing images</p>
                )}
              </div>

              <div style={{ marginTop: "24px" }}>
                <h4>Image Views - Upload New Images (Optional)</h4>
                <div className="image-views-grid">
                  {Object.keys(imageViewTypes).map((viewType) => (
                    <div key={viewType} className="image-view-card">
                      <h5>{viewType}</h5>
                      {imageViewTypes[viewType] ? (
                        <div style={{ position: "relative" }}>
                          <img
                            src={URL.createObjectURL(imageViewTypes[viewType])}
                            alt={viewType}
                            style={{
                              width: "100%",
                              height: "150px",
                              objectFit: "cover",
                              borderRadius: "8px",
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => removeImageFromView(viewType)}
                            style={{
                              position: "absolute",
                              top: "4px",
                              right: "4px",
                              background: "rgba(0,0,0,0.5)",
                              color: "white",
                              border: "none",
                              borderRadius: "50%",
                              width: "32px",
                              height: "32px",
                              fontSize: "18px",
                              cursor: "pointer",
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <label className="upload-label">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) =>
                              handleImageUploadForView(
                                viewType,
                                e.target.files && e.target.files[0]
                              )
                            }
                            style={{ display: "none" }}
                          />
                          <div className="upload-icon">📷</div>
                          <div className="upload-text">Click to upload</div>
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={goToPrevStep}>
                  Back
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? "Updating..." : "Complete Update"}
                </button>
              </div>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
